import handwritingPaths from './handwriting-paths.json';
import { useEffect, useId, useRef, useState } from 'react';

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!window.IntersectionObserver) { setVisible(true); return; }
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motion.matches) { setVisible(true); return; }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.08, rootMargin: '0px 0px -24px 0px' });
    observer.observe(ref.current);
    const onPreference = () => { if (motion.matches) { setVisible(true); observer.disconnect(); } };
    motion.addEventListener('change', onPreference);
    return () => { observer.disconnect(); motion.removeEventListener('change', onPreference); };
  }, []);
  return { ref, visible };
}

export function Reveal({ as: Element = 'div', children, className = '', delay = 0, style, ...props }) {
  const { ref, visible } = useReveal();
  return <Element {...props} ref={ref} className={`reveal-element ${className}`} data-visible={visible} style={{ ...style, '--reveal-delay': `${delay}ms` }}>{children}</Element>;
}

export function RevealWords({ children }) {
  const { ref, visible } = useReveal();
  let word = 0;
  return <span ref={ref} className="reveal-words" data-visible={visible}>{String(children).split(/(\s+)/).map((token, i) => /^\s+$/.test(token) ? token : <span key={i} className="reveal-word" style={{ '--word-delay': `${Math.min(word++ * 70, 490)}ms` }}>{token}</span>)}</span>;
}

// Font-outline fills are revealed by animated centerline pen strokes, letter by letter.
export function Handwriting({ children, delay = 350, variant = 'signature' }) {
  const { ref, visible } = useReveal();
  const id = useId().replace(/:/g, '');
  const text = String(children);
  const font = handwritingPaths[variant];
  let elapsed = delay;
  return <span ref={ref} className="handwriting" data-visible={visible}>
    <span className="handwriting-accessible">{text}</span>
    <span className="handwriting-visual" aria-hidden="true">{text.split(/(\s+)/).map((word, wordIndex) => {
      if (/^\s+$/.test(word)) { elapsed += 65; return word; }
      const shape = font?.words[word];
      if (!shape) return <span key={wordIndex}>{word}</span>;
      return <svg key={wordIndex} className="handwriting-word" data-word={word} viewBox={`0 -1000 ${shape.width} 1000`} style={{ width: `${shape.width / 1000}em` }} focusable="false">
        {shape.letters.map(({ char, x }, letterIndex) => {
          const glyph = font.glyphs[char];
          const maskId = `ink-${id}-${wordIndex}-${letterIndex}`;
          const length = glyph.strokes.reduce((sum, stroke) => sum + stroke.length, 0);
          const duration = Math.max(90, Math.min(300, length * .3));
          const strokes = glyph.strokes.map((stroke, strokeIndex) => {
            const start = elapsed;
            const time = Math.max(12, duration * stroke.length / length);
            elapsed += time;
            return <path key={strokeIndex} className="pen-stroke" d={stroke.d} fill="none" stroke="white" strokeWidth={stroke.width} strokeLinecap="round" strokeLinejoin="round" pathLength="1" style={{ '--pen-delay': `${start}ms`, '--pen-duration': `${time}ms` }} />;
          });
          const finish = elapsed;
          elapsed += 18;
          return <g key={letterIndex} transform={`translate(${x} 0)`}>
            <defs><mask id={maskId} maskUnits="userSpaceOnUse" x="-1500" y="-2000" width="4000" height="4000">{strokes}</mask></defs>
            <path d={glyph.outline} fill="currentColor" mask={`url(#${maskId})`} />
            <path className="ink-finish" d={glyph.outline} fill="currentColor" style={{ '--pen-delay': `${finish}ms` }} />
          </g>;
        })}
      </svg>;
    })}</span>
  </span>;
}
